import { useSelector } from "react-redux";
import { useMemo, useCallback } from "react";
const DEBUG = process.env.NODE_ENV === 'development';

export const usePermission = () => {
  const { user } = useSelector((state) => state.authReducer);


  const debugPermission = useCallback((_module, action, result, extra = {}) => {
    if (!DEBUG) return;

    const styles = {
      group: 'color: #666; font-weight: bold;',
      module: 'color: #2196F3; font-weight: bold;',
      action: 'color: #4CAF50; font-weight: bold;',
      success: 'color: #4CAF50;',
      denied: 'color: #f44336;',
      warning: 'color: #ff9800;'
    };

    // console.groupCollapsed(
    //   `%cPermission Check: %c${_module} %c${action}`,
    //   styles.group,
    //   styles.module,
    //   styles.action
    // );

    // console.log(
    //   `%cResult: ${result ? 'GRANTED ✅' : 'DENIED ❌'}`,
    //   result ? styles.success : styles.denied
    // );

    // console.log('User:', user);
    // console.log('Roles:', user?.roles);
    // console.log('Cache Hit:', extra.cacheHit ? '✅' : '❌');
    // console.log('Execution Time:', `${extra.executionTime}ms`);

    if (!result) {
      console.warn(
        '%cRequired Permission Missing',
        styles.warning,
        `\nModule: ${_module}\nAction: ${action}`
      );
    }

    console.groupEnd();
  }, [user]);

  // Cache user roles for performance
  const userRoles = useMemo(() => user?.roles || [], [user]);

  // Cache permissions map for O(1) lookup
  const permissionsMap = useMemo(() => {
    const map = new Map();

    if (!userRoles.length) return map;

    userRoles.forEach(role => {
      if (!role.permissions) return;

      role.permissions.forEach(permission => {
        // const key = `${permission.module}:${permission.actions.join(',')}`;
        // map.set(key, true);
        map.set(permission.module, permission.actions);
      });
    });

    // console.log('Permissions Map:', map);
    return map;
  }, [userRoles]);

  // Optimized permission check function
  const hasPermission = useCallback((_module, action) => {
    const start = performance.now();
    try {
      if (!user || !userRoles.length) {
        debugPermission(_module, action, false, {
          executionTime: performance.now() - start,
          cacheHit: false
        });
        return false;
      }

      // Get all actions for this module
      const moduleActions = permissionsMap.get(_module);
      if (!moduleActions) {
        debugPermission(_module, action, false, {
          executionTime: performance.now() - start,
          cacheHit: false
        });
        return false;
      }


      const actionSet = new Set(moduleActions);
      const result = Array.isArray(action)
        ? action.some(act => actionSet.has(act))
        : actionSet.has(action);

      if (DEBUG) {
        // console.log('Module Actions:', moduleActions);
        // console.log('Checking Action:', action);
        // console.log('Match Found:', result);
      }

      debugPermission(_module, action, result, {
        executionTime: performance.now() - start,
        cacheHit: Boolean(moduleActions)
      });

      return result;

    } catch (error) {
      console.error(
        '%cPermission Check Error 🔴',
        'color: #f44336; font-weight: bold;',
        { module: _module, action, error }
      );

      return false;
    }
  }, [user, userRoles, permissionsMap, debugPermission]);

  // Batch permission check for multiple actions
  const hasPermissions = useCallback((_module, actions) => {
    try {
      return actions.every(action => hasPermission(_module, action));
    } catch (error) {
      console.error('Batch permission check error:', { module: _module, actions, error });
      return false;
    }
  }, [hasPermission]);

  // Check if user has any permission for a module
  const hasModuleAccess = useCallback((_module) => {
    try {
      return Array.from(permissionsMap.keys())
        .some(key => key.startsWith(`${_module}:`));
    } catch (error) {
      console.error('Module access check error:', { module: _module, error });
      return false;
    }
  }, [permissionsMap]);

  return {
    hasPermission,
    hasPermissions,
    hasModuleAccess,
    userRoles
  };
};